import { useEffect, useState, useCallback } from 'react'
import { settingsRepository, initializeDatabase } from '../storage/db'

type Theme = 'light' | 'dark' | 'system'

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>('system')
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light')

  // Load saved theme on mount
  useEffect(() => {
    initializeDatabase().then(async () => {
      try {
        const settings = await settingsRepository.get()
        setThemeState(settings.theme)
        const resolved = settings.theme === 'system' ? getSystemTheme() : settings.theme
        setResolvedTheme(resolved)
        applyTheme(settings.theme)
      } catch {
        applyTheme('system')
        setResolvedTheme(getSystemTheme())
      }
    })
  }, [])

  // Listen for system theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      if (theme === 'system') {
        const resolved = getSystemTheme()
        setResolvedTheme(resolved)
        applyTheme('system')
      }
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback(async (newTheme: Theme) => {
    setThemeState(newTheme)
    const resolved = newTheme === 'system' ? getSystemTheme() : newTheme
    setResolvedTheme(resolved)
    applyTheme(newTheme)

    // Persist to settings
    try {
      const settings = await settingsRepository.get()
      await settingsRepository.save({ ...settings, theme: newTheme })
    } catch {
      // Settings save failed silently
    }
  }, [])

  const toggleTheme = useCallback(() => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark'
    setTheme(next)
  }, [resolvedTheme, setTheme])

  return { theme, resolvedTheme, setTheme, toggleTheme }
}
